import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import JSPDF from "jspdf";
import autoTable, { RowInput, UserOptions } from "jspdf-autotable";

import {
	getExcelCurrencyFormat,
	createLogger,
	formatNumber,
} from "../../utils";
import { DateRange } from "../../types/DataRange";
import IResponseWithItems from "../../types/IResponse";
import { formatDate } from "../../pages/Reports/pages/AccountingReports/utils/formatForFile";
import { CalendarNamespace } from "../../components/common";
import { Base, Language } from "..";

const logger = createLogger({ name: "CounterpartyBillReport" });

type DataItem = CounterpartyBillReport.Model.DataItem;

/** Raw value of each table column (the table cells read the same fields) */
const getColumnValue = (
	columnId: string,
	item: DataItem,
): string | number | undefined => {
	switch (columnId) {
		case "erpou":
			return item.edrpou;
		case "account":
			return item.checkValue;
		case "companyName":
			return item.name;
		case "balanceFromDate":
			return item.accountAmountBefore;
		case "replenishmentUah":
			return item.replenishment;
		case "orderCount":
			return item.orderCount;
		case "orderAmountUah":
			return item.orderPrice;
		case "balanceToDate":
			return item.accountAmountAfter;
		case "periodBalanceUah":
			return item.amountPeriod;
		default:
			return undefined;
	}
};

const MONEY_COLUMNS = new Set([
	"balanceFromDate",
	"replenishmentUah",
	"orderAmountUah",
	"balanceToDate",
	"periodBalanceUah",
]);

const roundMoney = (value: number) => Math.round(value * 100) / 100;

class CounterpartyBillReport extends Base {
	static fromResponse(data: any): CounterpartyBillReport.Model {
		return data || [];
	}

	public static async getCounterpartyBillReport(
		params: CounterpartyBillReport.SearchOptions,
	): Promise<CounterpartyBillReport.GetCounterpartyBillReportResponse | null> {
		try {
			if (!params.dateRange) return null;

			const response = await this.request((prpc) =>
				prpc.theirsModel.report.getCounterpartyBillReport(params),
			);

			logger.info(
				"[getCounterpartyBillReport] getCounterpartyBillReport res",
				{
					response,
					params,
				},
			);
			if (!response) return null;

			return {
				...response,
				items: response?.items?.map(this.fromResponse),
			};
		} catch (error) {
			logger.error("[getCounterpartyBillReport] Error", error);

			return null;
		}
	}

	public static exportPDF = async (
		data: CounterpartyBillReport.Model.Item[],
		options: {
			tableColumn: string[];
			columnStyles: UserOptions["columnStyles"];
			width: number;
			height: number;

			ColumnId: any;
			columnIds: any;
			textTranslations: Record<string, string>;
			headerTitle: string;
		},
	) => {
		if (!data?.length) return;
		const {
			tableColumn = [],
			width = 3500,
			height = 780,

			columnStyles,
			columnIds,
			textTranslations,
			headerTitle,
		} = options;

		const totalWidth = columnStyles
			? Object.values(columnStyles).reduce(
					(sum, { cellWidth }) => sum + +(cellWidth || 0),
					0,
			  ) +
			  Object.keys(columnStyles).length * (5.1 / 3.56) +
			  0.1 / 3
			: width;

		const realWidth = totalWidth || width;
		const doc = new JSPDF({
			orientation: "landscape",
			format: [realWidth, height],
		});

		let currentY = 10;
		let _dateFrom;
		let _dateTo;

		const _dataForExport = data?.map((e) => e.data) ?? [];

		doc.setFont("Roboto-Regular", "normal");

		const { dateRange } = data[0];

		const dateFrom = formatDate({
			date: dateRange.from,
			isTitleFormat: true,
		});

		const dateTo = formatDate({
			date: dateRange.to,
			isTitleFormat: true,
		});

		if (!_dateFrom) _dateFrom = dateFrom;
		if (!_dateTo) _dateTo = dateTo;

		const pageWidth = doc.internal.pageSize.getWidth();
		const centerX = pageWidth / 2;

		const textArray = [
			`${textTranslations.forPeriod} ${textTranslations.from} ${dateFrom} ${textTranslations.to} ${dateTo}`,
		];

		textArray.forEach((text) => {
			const textWidth = doc.getTextWidth(text);

			const textX = centerX - textWidth / 2;

			doc.text(text, textX, currentY);

			currentY += 10;
		});

		const tableRows = _dataForExport.map(
			(order) =>
				(columnIds as string[]).map((columnId) => {
					const value = getColumnValue(columnId, order);
					if (MONEY_COLUMNS.has(columnId)) {
						return { content: formatNumber(value as number) ?? "" };
					}
					return { content: `${value ?? ""}` };
				}) as RowInput,
		);

		autoTable(doc, {
			head: [tableColumn],
			body: tableRows,
			startY: currentY,
			styles: {
				fontSize: 12,
				cellPadding: 5,
				lineWidth: 0.1,
				lineColor: [0, 0, 0],
				font: "Roboto-Regular",
				fontStyle: "normal",
			},
			columnStyles,
			headStyles: {
				fillColor: [41, 128, 185],
				textColor: [255, 255, 255],
				fontSize: 10,
			},
			margin: { top: 20 },
			tableLineWidth: 0,
			tableLineColor: [0, 0, 0],
			theme: "grid",
		});

		currentY = doc?.lastAutoTable?.finalY;

		doc.setFont("Roboto-Regular", "normal");

		currentY += 50;

		const namefile = `${headerTitle ?? ""} - ${_dateFrom ?? ""} - ${
			_dateTo ?? ""
		}.pdf`;

		doc.save(namefile);
	};

	public static exportExcel = async (
		_data: CounterpartyBillReport.Model.Item[],
		options: {
			tableColumn: string[];
			columnStyles: UserOptions["columnStyles"];
			width: number;
			height: number;

			ColumnId: any;
			columnIds: any;
			textTranslations: Record<string, string>;
			headerTitle: string;

			dateFrom: CalendarNamespace.Value;
			dateTo: CalendarNamespace.Value;
		},
	) => {
		if (!_data?.length) return;

		const {
			tableColumn = [],

			columnStyles,
			columnIds,
			textTranslations,
			headerTitle,

			dateFrom,
			dateTo,
		} = options;

		const _dateFrom = formatDate({
			date: dateFrom ?? new Date(),
			isTitleFormat: true,
		});
		const _dateTo = formatDate({
			date: dateTo ?? new Date(),
			isTitleFormat: true,
		});

		const namefile = `${headerTitle ?? ""} - ${_dateFrom ?? ""} - ${
			_dateTo ?? ""
		}`;

		const workbook = new ExcelJS.Workbook();

		const createReport = async (
			data: CounterpartyBillReport.Model.Item[],
		) => {
			const CURRENCY_DEFAULT = "₴"; // ! Here we need a dynamic currency. We need to get the currency value from the back
			const worksheet = workbook.addWorksheet(namefile);

			tableColumn.forEach((colIndex, index) => {
				const cellWidth: number = +(
					columnStyles?.[index]?.cellWidth ?? 80
				);

				if (typeof cellWidth === "number" && cellWidth > 0) {
					worksheet.getColumn(index + 1).width = Math.round(
						cellWidth / 2.5,
					);
				} else {
					logger.warn(
						`2 Width for column ${colIndex} is undefined or invalid: cellWidth`,
						cellWidth,
					);
				}
			});

			const _dataForExport = data?.map((e) => e.data) ?? [];

			const { dateRange } = data[0];

			const dateFrom = formatDate({
				date: dateRange.from,
				isTitleFormat: true,
			});
			const dateTo = formatDate({
				date: dateRange.to,
				isTitleFormat: true,
			});

			const addHeaderRow = (text: string) => {
				const headerRow = worksheet.addRow([text]);
				headerRow.font = {
					size: 10,
					name: "Arial",
				};
				headerRow.alignment = {
					horizontal: "left",
					vertical: "middle",
					wrapText: false,
				};

				headerRow.height = 30;

				worksheet.mergeCells(
					`A${headerRow.number}:F${headerRow.number}`,
				);

				headerRow.getCell(1).alignment = {
					horizontal: "center",
					vertical: "middle",
				};
			};

			const textheaderTable = [
				`${textTranslations.forPeriod} ${textTranslations.from} ${dateFrom} ${textTranslations.to} ${dateTo}`,
			];

			textheaderTable.forEach((text) => {
				addHeaderRow(text);
			});

			const titleRow = worksheet.addRow(tableColumn);
			titleRow.font = {
				name: "Calibri",
				size: 11,
				color: { argb: "ffffff" },
			};
			titleRow.alignment = {
				horizontal: "center",
				vertical: "middle",
				wrapText: true,
			};
			titleRow.fill = {
				type: "pattern",
				pattern: "solid",
				fgColor: { argb: "4baac6" },
			};
			titleRow.border = {
				top: { style: "thin", color: { argb: "FF000000" } },
				bottom: { style: "thin", color: { argb: "FF000000" } },
			};

			const maxHeight = Math.min(
				3,
				Math.max(
					...tableColumn.map((cell) => {
						if (typeof cell === "string") {
							return Math.ceil(cell.length / 10);
						}
						return 1;
					}),
				),
			);

			titleRow.height = maxHeight * 13;

			_dataForExport.forEach((order, index) => {
				const ids = columnIds as string[];
				const row: (string | number | undefined | Date | null)[] =
					ids.map((columnId) => {
						const value = getColumnValue(columnId, order);
						if (MONEY_COLUMNS.has(columnId)) {
							return typeof value === "number"
								? roundMoney(value)
								: null;
						}
						return value ?? "";
					});

				function isOdd(num: number): boolean {
					return num % 2 !== 0;
				}

				const newRow = worksheet.addRow(row);
				newRow.alignment = {
					horizontal: "center",
					vertical: "middle",
					wrapText: true,
				};
				newRow.font = { name: "Arial", size: 10 };

				ids.forEach((columnId, columnIndex) => {
					const cell = newRow.getCell(columnIndex + 1);
					if (MONEY_COLUMNS.has(columnId)) {
						cell.numFmt = getExcelCurrencyFormat(CURRENCY_DEFAULT);
						cell.alignment = { wrapText: true };
					} else if (columnId === "orderCount") {
						cell.numFmt = "0";
					}
				});

				if (isOdd(index)) {
					newRow.fill = {
						type: "pattern",
						pattern: "solid",
						fgColor: { argb: "d9d9d9" },
					};
				}
				if (_dataForExport.length - 1 === index) {
					newRow.border = {
						bottom: {
							style: "thin",
							color: { argb: "FF000000" },
						},
					};
				}
				const maxHeight = Math.min(
					3,
					Math.max(
						...row.map((cell) => {
							if (typeof cell === "string") {
								return Math.ceil(cell.length / 10);
							}
							return 1;
						}),
					),
				);

				newRow.height = maxHeight * 13;

				newRow.alignment = {
					horizontal: "center",
					vertical: "middle",
					wrapText: true,
				};
			});

			worksheet.addRow([]);
			worksheet.addRow([]);

			const buffer = await workbook.xlsx.writeBuffer();

			const blob = new Blob([buffer], {
				type: "application/octet-stream",
			});
			saveAs(blob, nameFileExcelOnly);
		};

		const nameFileExcelOnly = `${namefile}.xlsx`;

		createReport(_data);
	};
}

declare namespace CounterpartyBillReport {
	type Model = CounterpartyBillReport.Model.Item;

	interface GetCounterpartyBillReportResponse
		extends IResponseWithItems<Model> {}

	interface SearchOptions {
		counterpartyIds?: number[];
		checkIds?: number[];
		taxiServiceIds?: number[];
		general?: boolean;
		dateRange: DateRange;
		lang: Language;

		allCounterparties?: boolean;
	}

	namespace Model {
		interface DataItem {
			counterpartyId: number;
			edrpou?: string;
			checkValue: string;
			name: string;
			accountAmountAfter?: number;
			replenishment: number;
			orderCount: number;
			orderPrice: number;
			accountAmountBefore?: number;
			amountPeriod: number;
		}
		interface Item {
			dateRange: DateRange;
			data: DataItem;
		}
	}
}

export default CounterpartyBillReport;
